import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "tack_cookie_consent";
const OPEN_PREFS_EVENT = "tack:open-cookie-prefs";

export type CookieConsent = {
  essential: true;
  analytics: boolean;
  timestamp: string;
};

const NON_EUROPE_EU_TZ = new Set([
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Atlantic/Canary",
  "Atlantic/Reykjavik", // Iceland — EEA
]);

function isEuTimezone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Europe/")) {
      // Exclude clearly non-EU European timezones
      const nonEu = new Set([
        "Europe/Moscow",
        "Europe/Kaliningrad",
        "Europe/Samara",
        "Europe/Volgograd",
        "Europe/Saratov",
        "Europe/Ulyanovsk",
        "Europe/Astrakhan",
        "Europe/Kirov",
        "Europe/Istanbul",
        "Europe/Minsk",
        "Europe/Kiev",
        "Europe/Simferopol",
      ]);
      return !nonEu.has(tz);
    }
    return NON_EUROPE_EU_TZ.has(tz);
  } catch {
    return false;
  }
}

/** Plain (non-hook) check for EU/EEA visitors via timezone heuristic. */
export function isEuVisitor(): boolean {
  return isEuTimezone();
}

/** Plain (non-hook) read of analytics consent from localStorage. */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.analytics === true;
  } catch {
    return false;
  }
}

function readConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.analytics === "boolean") return parsed as CookieConsent;
    return null;
  } catch {
    return null;
  }
}

function writeConsent(analytics: boolean) {
  if (typeof window === "undefined") return;
  const payload: CookieConsent = {
    essential: true,
    analytics,
    timestamp: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Hook for any future analytics call to gate on consent. */
export function useAnalyticsConsent(): boolean {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    const c = readConsent();
    setAllowed(!!c?.analytics);
  }, []);
  return allowed;
}

/** Programmatic opener for the preferences dialog (used by footer links). */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}

export function CookieBanner() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => {
    setMounted(true);
    const existing = readConsent();
    if (existing) {
      setAnalyticsOn(existing.analytics);
    } else if (isEuTimezone()) {
      setShowBanner(true);
    }

    const onOpen = () => {
      const cur = readConsent();
      setAnalyticsOn(!!cur?.analytics);
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, onOpen);
  }, []);

  if (!mounted) return null;

  const acceptAll = () => {
    writeConsent(true);
    setAnalyticsOn(true);
    setShowBanner(false);
    setPrefsOpen(false);
  };
  const essentialOnly = () => {
    writeConsent(false);
    setAnalyticsOn(false);
    setShowBanner(false);
    setPrefsOpen(false);
  };
  const savePrefs = () => {
    writeConsent(analyticsOn);
    setShowBanner(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-label={t("cookies.prefsTitle")}
          className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-4 sm:pb-4"
        >
          <div className="mx-auto max-w-3xl rounded-xl border border-border bg-background shadow-lg p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Cookie className="size-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  {t("cookies.title")}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {t("cookies.body")}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={acceptAll}>
                    {t("cookies.acceptAll")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={essentialOnly}>
                    {t("cookies.essentialOnly")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPrefsOpen(true)}
                  >
                    {t("cookies.manage")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("cookies.prefsTitle")}</DialogTitle>
            <DialogDescription>
              {t("cookies.prefsDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t("cookies.essential")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("cookies.essentialDesc")}
                </p>
              </div>
              <Switch checked disabled aria-label={t("cookies.essential")} />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t("cookies.analytics")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("cookies.analyticsDesc")}
                </p>
              </div>
              <Switch
                checked={analyticsOn}
                onCheckedChange={setAnalyticsOn}
                aria-label={t("cookies.analytics")}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={essentialOnly}>
              {t("cookies.essentialOnly")}
            </Button>
            <Button onClick={savePrefs}>{t("cookies.savePrefs")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
